/**
 * 5教科「まなびモード」の基本型(docs/10_paltown_platform.md §2)。
 *
 * 描画層・Firebase・Phaser に依存しない(CLAUDE.md ルール1 と同じ鉄則)。
 * `packages/engine`(タイピング)・`packages/puzzle`(思考力)と並ぶ第3のモジュール。
 *
 * **用語**:
 *   - `QuizSubject` = 教科。帯(band)ごとに呼び名が変わる(幼児は「かず/もじ/ちえ」)
 *   - `QuizUnit`    = 単元。教科の中の学習順のまとまり(「5までのかず」など)。1単元10問前後
 *   - `QuizQuestion`= 1問。単元に属する
 *
 * 問題データそのものは**このパッケージには持たない**。
 * `tools/content-build/data/curated/quiz-*.json` を原本とし、
 * ビルドで `content/v1/quiz/{band}.json`(= `QuizBank`)へ出す。
 */

import type { QuizLesson } from './lesson.js';

/** コンテンツJSONのスキーマ版(content-build の CONTENT_SCHEMA_VERSION と揃える)。 */
export const QUIZ_SCHEMA_VERSION = 1;

/**
 * 対象帯。学年そのものではなく「同じ難度で括れるまとまり」。
 * 幼児・小1〜小6。`e3`〜`e6` は**登録だけ**済んでいて、
 * 配信データ(`content/v1/quiz/{band}.json`)が出来た帯から順に遊べるようになる
 * (画面の「あそべる帯」は manifest から動的に決める。docs/13 §3)。
 */
export type QuizBand = 'kinder' | 'e1' | 'e2' | 'e3' | 'e4' | 'e5' | 'e6';

/** すべての帯(UI の並び順もこの順)。 */
export const QUIZ_BANDS: readonly QuizBand[] = ['kinder', 'e1', 'e2', 'e3', 'e4', 'e5', 'e6'];

/** 帯の表示名(ひらがな。CLAUDE.md ルール5)。 */
export const QUIZ_BAND_LABELS: Readonly<Record<QuizBand, string>> = {
  kinder: 'ようじ',
  e1: 'しょうがく1ねん',
  e2: 'しょうがく2ねん',
  e3: 'しょうがく3ねん',
  e4: 'しょうがく4ねん',
  e5: 'しょうがく5ねん',
  e6: 'しょうがく6ねん',
};

/**
 * 教科。幼児帯は教科名を就学前の呼び名にする(「さんすう」ではなく「かず」)。
 * 表示名がそのまま識別子(小学生に見せる文字列と1:1で、翻訳キーを増やさない)。
 */
export type QuizSubject =
  'かず' | 'もじ' | 'ちえ' | 'さんすう' | 'こくご' | 'せいかつ' | 'りか' | 'しゃかい';

/** すべての教科(帯の順 → 帯内の並び順)。 */
export const QUIZ_SUBJECTS: readonly QuizSubject[] = [
  'かず',
  'もじ',
  'ちえ',
  'さんすう',
  'こくご',
  'せいかつ',
  'りか',
  'しゃかい',
];

/**
 * 帯ごとの教科(この対応から外れた組み合わせはスキーマ検証で弾く)。
 *
 * 小学生の帯は同じ教科名を共有する(小1の「さんすう」と小2の「さんすう」は
 * 教科としては同じもので、difficulty が違うだけ)。単元と問題は帯ごとに分かれる。
 */
export const QUIZ_SUBJECTS_BY_BAND: Readonly<Record<QuizBand, readonly QuizSubject[]>> = {
  kinder: ['かず', 'もじ', 'ちえ'],
  e1: ['さんすう', 'こくご', 'せいかつ'],
  e2: ['さんすう', 'こくご', 'せいかつ'],
  // 小3から「せいかつ」が「りか」「しゃかい」に分かれる(docs/13 §2)。
  e3: ['さんすう', 'こくご', 'りか', 'しゃかい'],
  e4: ['さんすう', 'こくご', 'りか', 'しゃかい'],
  e5: ['さんすう', 'こくご', 'りか', 'しゃかい'],
  e6: ['さんすう', 'こくご', 'りか', 'しゃかい'],
};

/**
 * 問題文(`prompt.text`)に漢字を使ってよい帯。
 *
 * 未就学児〜小1はまだ漢字を読めないので**ひらがな・カタカナだけ**で書く。
 * 小2からは「漢字を読む」こと自体が単元になるため、`prompt.ruby`(ふりがな)を
 * 必ず添えることを条件に漢字を許す(CLAUDE.md ルール5 の「漢字にはふりがな」)。
 *
 * 解説・単元名はどの帯でも漢字を使えない —— ふりがなを振る場所が無いため。
 * 選択肢は e3 以降だけ例外(`QUIZ_KANJI_CHOICE_BANDS`)。
 *
 * **ここが縛るのは かな版だけ**(ユーザー指示 2026-08-25)。漢字版の控え
 * (`textKanji` / `explanationKanji`)は必ずふりがな付きで出すので、
 * kinder / e1 を含む**全帯**が持てる。
 */
export const QUIZ_KANJI_PROMPT_BANDS: readonly QuizBand[] = ['e2', 'e3', 'e4', 'e5', 'e6'];

/** この帯の問題文に漢字を書いてよいか(書くなら `ruby` が必須)。 */
export function allowsKanjiPrompt(band: QuizBand): boolean {
  return QUIZ_KANJI_PROMPT_BANDS.includes(band);
}

/**
 * **選択肢**に漢字を使ってよい帯(docs/13 §3 のこくご「漢字をえらぼう」)。
 *
 * 選択肢にはふりがなを振る場所が無い ―― というのが kinder〜e2 の理由だったが、
 * 小3以降は「よみ → ただしい漢字をえらぶ」こと自体が単元になる。
 * ここでふりがなを振ると**答えがそのまま漏れる**ので、
 * 「漢字は書けるが、ふりがなは付けない」を帯のルールとして許す。
 * 解説はどの帯でも かな のみ(まちがえた子が読めないと意味がない)。
 *
 * **ここが縛るのも かな版だけ**。漢字版の控え(`choices[].textKanji`)は
 * ふりがな(`textKanjiSegments`)付きで出すので全帯が持てる。
 */
export const QUIZ_KANJI_CHOICE_BANDS: readonly QuizBand[] = ['e3', 'e4', 'e5', 'e6'];

/** この帯の選択肢に漢字を書いてよいか(ふりがなは付けない)。 */
export function allowsKanjiChoice(band: QuizBand): boolean {
  return QUIZ_KANJI_CHOICE_BANDS.includes(band);
}

/**
 * 出題形式。
 *
 * `choice` = ふつうの選択式(2〜4択)
 * `count`  = かず数え。`prompt.emoji` の絵を数えて、数字の選択肢から選ぶ
 *
 * どちらも操作は「タップして選ぶ」だけ(docs/11 §1 と同じ設計原則)。
 * 自由入力の `input` 型は次期(キーボード入力はタイピングモジュールの領分)。
 */
export type QuizQuestionType = 'choice' | 'count';

/** すべての出題形式。 */
export const QUIZ_QUESTION_TYPES: readonly QuizQuestionType[] = ['choice', 'count'];

/**
 * もじの ひょうじ(日本語の表記)。
 *
 * 学習言語に日本語が含まれる子は、設定でどちらかを選べる(既定は `kanji`):
 *   - `hiragana` … かな中心の `text` をそのまま出す
 *   - `kanji` …… 漢字版(`textKanji` / `explanationKanji`)があればそれを出す
 *
 * **問題データは表記を切り替える仕組みを知らない**。切り替えは
 * `withJapaneseScript()`(script.ts)がセッション組み立ての手前で行う。
 */
export type JapaneseScript = 'hiragana' | 'kanji';

/** すべての表記(設定の保存値の検証に使う)。 */
export const JAPANESE_SCRIPTS: readonly JapaneseScript[] = ['hiragana', 'kanji'];

/**
 * 既定の表記(未設定・壊れた保存値はここへ丸める)。
 *
 * **漢字**(ユーザー指示 2026-08-25)。漢字にはかならずふりがなが付くので
 * 読めなくて詰まることは無く、日本語の文としては こちらが自然な見た目になる。
 * かなで読みたい子は設定で `hiragana` に戻せる(保存済みの選択はそのまま尊重される)。
 */
export const DEFAULT_JAPANESE_SCRIPT: JapaneseScript = 'kanji';

/**
 * ふりがなの**区切り**1つ(docs/13 §12)。
 *
 * 「文まるごとに1本のふりがな」だと、ひらがなの上にもふりがなが乗ってしまう。
 * そこで配信JSONは本文を**漢字のかたまり**と**それ以外**に切り分けて持ち、
 * 画面は漢字のかたまりにだけ `<ruby>` を掛ける。
 *
 * 切り分けは**ビルド時**に決める(実行時に読みを推測しない)。
 * つなげると本文にもどることをスキーマ検証が確かめる。
 */
export interface QuizTextSegment {
  /** 本文の一部(すべてつなげると本文と1文字たがわず一致する)。 */
  readonly t: string;
  /**
   * この部分のふりがな(かなのみ)。
   * **漢字を含む部分にだけ**付く。かな・数字・記号だけの部分には付けない。
   */
  readonly r?: string;
}

/**
 * 絵文字の代わりに出す**ステッカー画像**のひとかたまり(docs/05 §2b の生成物)。
 *
 * `emoji` が `"🍎🍎🍎"` なら `{ file: 'img/emoji/apple.webp', count: 3 }` の1個。
 * **同じ絵を `count` 個ならべる**のが画面の仕事で、数える問題(`count` 型)は
 * この並びがそのまま「数える対象」になる。
 */
export interface QuizEmojiImagePart {
  /** 画像の相対パス(`assets/` から。例 `img/emoji/apple.webp`)。 */
  readonly file: string;
  /** 同じ絵をいくつならべるか(1以上)。 */
  readonly count: number;
}

/**
 * 絵と絵のあいだの**つなぎことば**(「と」)。
 *
 * `"🍓🍓🍓 と 🐈🐈"` のような問題は「と」が消えると意味が変わる
 * (合わせていくつ / どちらが多い)ので、絵にできない文字はそのまま持ち越す。
 */
export interface QuizEmojiTextPart {
  readonly text: string;
}

/** `emojiImages` の1要素。画像のかたまりか、つなぎことばのどちらか。 */
export type QuizEmojiPart = QuizEmojiImagePart | QuizEmojiTextPart;

/** `QuizEmojiPart` が画像のかたまりか。 */
export function isQuizEmojiImagePart(part: QuizEmojiPart): part is QuizEmojiImagePart {
  return (part as QuizEmojiImagePart).file !== undefined;
}

/** 問題文。 */
export interface QuizPrompt {
  /** 問題文(ひらがな優先。漢字を使うなら `ruby` が必須)。 */
  readonly text: string;
  /**
   * **漢字版**の問題文(任意)。設定が `kanji` のときだけ `text` の代わりに出す。
   *
   * かな版(`text`)が唯一の原本で、こちらは**同じ内容を漢字で書き直した控え**。
   * 表示のときは かな版が そのまま ふりがな になる(`ruby` が無ければ `text` を使う)ので、
   * 漢字が読めない子が うっかり選んでも詰まらない。
   *
   * **読みを問う問題(`rubyHidden`)には付けられない**(schema が弾く):
   * 漢字版を出した時点で答えが漏れるため。それ以外は**全帯**が持てる
   * (ふりがなが必ず付くので kinder / e1 でも読めなくて詰まることは無い)。
   */
  readonly textKanji?: string;
  /** ふりがな(かなのみ)。`text` に漢字があるとき必須。 */
  readonly ruby?: string;
  /**
   * `text` を**漢字のかたまり単位**に切り分けたもの(任意)。
   *
   * あれば画面は漢字の部分にだけ `<ruby>` を掛ける。無ければ従来どおり
   * 文まるごとに `ruby` を1本振る(壊さないためのフォールバック)。
   * `rubyHidden`(読みを問う問題)には**付けない** —— 答えが漏れるため。
   */
  readonly textSegments?: readonly QuizTextSegment[];
  /** `textKanji` を切り分けたもの(任意)。漢字表示に切り替えたときに使う。 */
  readonly textKanjiSegments?: readonly QuizTextSegment[];
  /**
   * `ruby` を**画面に出さない**(読み上げ用のテキストとしてだけ使う)。
   *
   * 漢字の読み方そのものを問う問題(「山の よみかたは どれ?」)では、
   * ふりがなを出すと答えがそのまま見えてしまう。そこで `ruby` には
   * 「この かんじの よみかたは どれ?」のような**答えを含まない読み**を入れ、
   * このフラグで表示を止める。`ruby` が無いときは意味を持たない。
   */
  readonly rubyHidden?: boolean;
  /**
   * 問題文に添える絵(`count` 型の数える対象など。例 `"🍎🍎🍎"`)。
   * 絵文字が主だが、`"🍎🍎 と 🍌"` のような短いつなぎことばは入れてよい。
   * 画像アセットができるまでの暫定表現 —— カタログの `emoji` と同じ扱い。
   * **読み上げ音声には載せない**ので、`text` は絵なしでも意味が通るように書く。
   */
  readonly emoji?: string;
  /**
   * `emoji` を**ステッカー画像の列**に置き換えたもの(任意。ビルド時に決まる)。
   *
   * 画面は これがあれば絵文字の代わりに画像を出し、無ければ `emoji` をそのまま出す。
   * **`emoji` は消さない** —— 画像が配備されていない環境・読み込み失敗の受け皿であり、
   * 読み上げ音声の中身(`emoji` を読まない約束)も変わらない。
   * 変換表は `tools/asset-pipeline/data/emoji-images.json`(絵文字 → slug → ファイル)。
   */
  readonly emojiImages?: readonly QuizEmojiPart[];
  /**
   * 問題の絵(`assets/` からの相対パス。例 `img/clock/clock-3-30.webp`)。
   * 設定すると Web は**大きく**表示する(時計を読む問題の主役はこの画像)。
   * `emoji` と併用してもよい(絵文字は画像が無いときの代役)。
   */
  readonly image?: string;
  /**
   * 画像の代替テキスト(スクリーンリーダー・画像が出ないとき)。
   *
   * **答えを書かない**。「とけい」のような かなの汎用語だけにする
   * (「3じ30ぷん」と書くと、読み上げただけで答えが分かってしまう)。
   * 省略時は装飾扱い(`alt=""`)。
   */
  readonly imageAlt?: string;
  /**
   * 読み上げ音声の相対パス(`au/ja/{qid}.m4a`)。**全問題に付ける**(未就学児は文が読めない)。
   * asset-pipeline の `quiz-audio` が生成し、content-build が実在するものだけを埋める。
   */
  readonly audio?: string;
}

/** 選択肢1つ。`text` / `emoji` / `image` のうち少なくとも1つを持つ。 */
export interface QuizChoice {
  /**
   * 選択肢の文字(ひらがな・数字)。
   * 漢字は e3 以降だけ許す(`QUIZ_KANJI_CHOICE_BANDS`)。ふりがなは付けない。
   */
  readonly text?: string;
  /**
   * **漢字版**の選択肢(任意)。設定が `kanji` のときだけ `text` の代わりに出す。
   *
   * かな版(`text`)が読みそのものなので、画面は `textKanjiSegments` を使って
   * **漢字の部分にだけ**ふりがなを乗せる(ユーザー指示 2026-08-25)。
   * **読みを問う問題には付けない**(`rubyHidden` の問題は schema が弾く)。
   */
  readonly textKanji?: string;
  /**
   * `textKanji` を漢字のかたまり単位に切り分けたもの(任意。`QuizTextSegment`)。
   *
   * 読みは**かな版の `text` そのもの**。切り分けはビルド時
   * (`tools/content-build/src/quiz-ruby.ts`)に決まり、一意に割れなければ付かない。
   */
  readonly textKanjiSegments?: readonly QuizTextSegment[];
  /**
   * **表示用**の区切り(配信JSONには載らない)。
   *
   * `withJapaneseScript()` が漢字版へ切り替えるときに `textKanjiSegments` を
   * ここへ移す。画面は「`text` を `textSegments` があればその区切りで描く」だけを見ればよく、
   * どちらの表記で描いているのかを知らなくて済む(`QuizPrompt` と同じ作法)。
   */
  readonly textSegments?: readonly QuizTextSegment[];
  /** 選択肢の絵文字(例 `"🍎🍎🍎"`)。画像アセットができるまでの暫定表現。 */
  readonly emoji?: string;
  /** `emoji` をステッカー画像の列に置き換えたもの(`QuizPrompt.emojiImages` と同じ約束)。 */
  readonly emojiImages?: readonly QuizEmojiPart[];
  /**
   * 選択肢の絵(`assets/` からの相対パス)。
   * **文字が無い選択肢**(時計の絵だけを見比べる型)も成立する。
   */
  readonly image?: string;
  /** 画像の代替テキスト。`prompt.imageAlt` と同じルール(答えを書かない)。 */
  readonly imageAlt?: string;
  /**
   * 読み上げ音声の相対パス(`au/ja/{qid}-c{位置}.m4a`)。
   * 文字が読めない子が**選択肢だけ**を聞けるようにする(タップ=解答とは別の 🔊 ボタン)。
   * `text` を持つ選択肢にだけ付く。content-build が実在するものだけを埋める。
   */
  readonly audio?: string;
  /** 正解ならtrue。1問にちょうど1つ。 */
  readonly correct?: boolean;
}

/** 問題1問。 */
export interface QuizQuestion {
  /** 問題ID(例 `qk0001` = 幼児 / `q10001` = 小1)。全帯を通して一意。 */
  readonly id: string;
  readonly subject: QuizSubject;
  readonly band: QuizBand;
  readonly type: QuizQuestionType;
  readonly prompt: QuizPrompt;
  /** 2〜4択。`correct` はちょうど1つ。 */
  readonly choices: readonly QuizChoice[];
  /** やさしい一言解説(ひらがな)。まちがえたときに出す。 */
  readonly explanation?: string;
  /**
   * **漢字版**の解説(任意)。設定が `kanji` のときだけ `explanation` の代わりに出す。
   *
   * かな版の解説は「まちがえた子が読める」ことが絶対条件なので漢字を書けない
   * (`explanation` は全帯で かな のみ)。この控えは**漢字を選んだ子にだけ**出すので、
   * その制約の外にある。**全帯**が持てる(ふりがなが必ず付く)。
   */
  readonly explanationKanji?: string;
  /**
   * `explanationKanji` を漢字のかたまり単位に切り分けたもの(任意)。
   * 読みは**かな版の `explanation` そのもの**。
   */
  readonly explanationKanjiSegments?: readonly QuizTextSegment[];
  /**
   * **表示用**の区切り(配信JSONには載らない)。
   * `withJapaneseScript()` が漢字版へ切り替えるときに `explanationKanjiSegments` を移す。
   */
  readonly explanationSegments?: readonly QuizTextSegment[];
  /** 習熟タグ(例 `かぞえる-5まで`)。AIチューター・つまずき分析の materia(docs/07)。 */
  readonly skills: readonly string[];
  /** 所属する単元ID。 */
  readonly unit: string;
}

/**
 * 出題の**見せ方**(docs/15 §3 のプレゼンテーション層)。
 *
 * 問題データ(`QuizQuestion`)は見せ方を知らない。ステージ定義がこの値を持ち、
 * 画面側は「presentation → コンポーネント」のレジストリで解決する。
 * 契約は「問題を受け取り、解答(選択肢の提示順の添字)と経過時間を返す」だけで、
 * 採点・報酬・進捗は既存フローを共有する(サーバー検証も不変)。
 *
 * 見せ方はどれも**同じ問題・同じ採点**で、変わるのは画面の飾りだけ:
 *   - `basic`      … 1問1答(既定)
 *   - `rpg-battle` … 敵と戦う。せいかいでパルが こうげき・てきの たいりょくが1へる
 *   - `runner`     … パルが 左から 走る。せいかいで 1区間すすむ
 *
 * **飾りが採点や報酬を変えることは無い**(ペナルティも早解きボーナスも無い)。
 */
export type QuizPresentation = 'basic' | 'rpg-battle' | 'runner';

/** すべての見せ方(未知の値はスキーマ検証で弾く)。 */
export const QUIZ_PRESENTATIONS: readonly QuizPresentation[] = ['basic', 'rpg-battle', 'runner'];

/** 既定の見せ方(ステージ定義が省略したときの値)。 */
export const DEFAULT_QUIZ_PRESENTATION: QuizPresentation = 'basic';

/**
 * 単元内ステージ(docs/13 §11)。
 *
 * 単元の問題プールを**定義順のまま**10問ずつ区切った固定グループ。
 * ビルド時に確定して配信JSONに載る(`tools/content-build` が `splitQuizStages` で作る)。
 *
 * 1回のプレイは**このステージの問題を定義順で全問**(シャッフルしない)。
 * ステージnをクリア(★1以上)すると n+1 が解放される。
 */
export interface QuizStage {
  /** ステージID。必ず `{単元ID}-s{1始まりの番号}`(進捗キーになるので形を固定する)。 */
  readonly id: string;
  /** そのステージの問題ID(単元の並びの一部を、順序を保ったまま切り出したもの)。 */
  readonly questionIds: readonly string[];
  /** 見せ方(既定 `basic`)。 */
  readonly presentation: QuizPresentation;
}

/** 単元(教科の中の学習順のまとまり)。 */
export interface QuizUnit {
  /** 単元ID(例 `kazu-5made`)。帯を通して一意。 */
  readonly id: string;
  readonly band: QuizBand;
  readonly subject: QuizSubject;
  /** 単元名(ひらがな。例「5までのかず」)。 */
  readonly title: string;
  /** この単元の問題ID(出題順の原本)。 */
  readonly questionIds: readonly string[];
  /**
   * 単元内ステージ(docs/13 §11)。`questionIds` を定義順のまま10問ずつ切ったもの。
   *
   * **配信データには必ず入る**(content-build が付ける)が、型の上では省略可にしてある:
   * ステージ制より前に配られた JSON がキャッシュに残っていても画面が死なないよう、
   * 読み取り側は `quizUnitStages()` を通して**無ければその場で導出**する。
   */
  readonly stages?: readonly QuizStage[];
  /**
   * 単元冒頭の解説パート(docs/13 §8)。**持っている単元だけ**に付く。
   *
   * 無い単元では解説UIを一切出さない(「じゅんびちゅう」も出さない)。
   * 原本は `tools/content-build/data/curated/lessons/{単元ID}.json` で、
   * ビルドがここへ同梱して配信する(問題と同じJSONで届くので追加の通信が要らない)。
   */
  readonly lesson?: QuizLesson;
}

/** `content/v{N}/quiz/{band}.json` の中身。 */
export interface QuizBank {
  readonly schemaVersion: number;
  readonly band: QuizBand;
  /** この帯に出てくる教科(`units` の並び順から導出)。 */
  readonly subjects: readonly QuizSubject[];
  readonly units: readonly QuizUnit[];
  readonly questions: readonly QuizQuestion[];
}

/**
 * 教科が**最初に出てくる**帯(未知の教科なら undefined)。
 *
 * 小学生の帯は教科名を共有する(さんすうは e1 にも e2 にもある)ので、
 * 返るのは `QUIZ_BANDS` の並びで最初の帯 = その教科を習いはじめる学年。
 */
export function bandOfSubject(subject: QuizSubject): QuizBand | undefined {
  return QUIZ_BANDS.find((band) => QUIZ_SUBJECTS_BY_BAND[band].includes(subject));
}
